import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { take } from 'rxjs/operators';

import { LoginService } from '../../core/services/login.service';
import { AuthService } from '../../core/services/auth.service';
import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { ToastService } from '../../shared/services/toast.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { UserFacade } from '../../core/store/user/user.facade';
import { ExternalChannelAccessResponse } from '../../core/models/login.model';
import { normalizeMceRedirectProductCode } from '../../core/constants/mce-redirect-products.constants';
import { resolveMceRedirectDestinationPath } from '../../core/constants/mce-redirect-destinations.constants';
import { MceRedirectProductService } from '../../core/services/mce-redirect-product.service';
import { ExternalChannelParentMessageService } from '../../core/services/external-channel-parent-message.service';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { HeaderService } from '../../shared/services/header.service';
import { blockCartHydration } from '../../core/utils/cart-hydration.util';
import {
  clearExternalChannelSessionStorage,
  constructFinalRedirectWithToken,
  clearPendingOrdersToken,
  getExternalChannelMobileDocument,
  getPostAddressCompletionRoute,
  hydrateExternalChannelSession,
  isExternalChannelMobileType,
  persistPendingOrdersToken,
  readStoredExternalChannelCampaign,
  shouldCompleteAddressFirst,
  STORAGE_EXTERNAL_CHANNEL_ACCESS,
  STORAGE_EXTERNAL_CHANNEL_ORIGIN,
  STORAGE_REDIRECT_AFTER_LOGIN,
  validateTokenUUID,
  withPersistedExternalChannelOrigin,
} from '../../core/utils/external-channel-access.util';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import { ChannelEventsService } from '../../core/services/channel-events.service';
import { environment } from '../../../environments/environment';

const CLARO_EMPRESAS_HOME_URL = `${environment.mceRedirectUrl}/#/HomeLogada`;
const MCE_REDIRECT_AFTER_ERROR_DELAY_MS = 2500;

function isSafeInternalRedirect(value: string | null | undefined): value is string {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;
  // Rejeita URLs absolutas e URLs relativas ao protocolo
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(v)) return false;
  if (v.startsWith('//')) return false;
  return true;
}

function normalizeRedirect(value: string): string {
  const v = value.trim();
  return v.startsWith('/') ? v : `/${v}`;
}

@Component({
  selector: 'umkt-external-channel-access',
  standalone: true,
  imports: [CommonModule, RouterModule, AuthLayoutComponent, LoadingComponent, I18nPipe],
  templateUrl: './external-channel-access.component.html',
  styleUrls: ['../../../assets/style/login-register.component.scss'],
})
export class ExternalChannelAccessComponent implements OnInit {
  loading = true;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private userFacade = inject(UserFacade);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private mceRedirectProductService = inject(MceRedirectProductService);
  private externalChannelParentMessageService = inject(ExternalChannelParentMessageService);
  private cartFacade = inject(CartFacade);
  private headerService = inject(HeaderService);
  private channelEventsService = inject(ChannelEventsService);
  private platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    const authPayload = this.route.snapshot.paramMap.get('authPayload');
    const origin = this.route.snapshot.queryParamMap.get('origin');
    const redirectRaw = this.route.snapshot.queryParamMap.get('redirect');
    const tokenRaw = this.route.snapshot.queryParamMap.getAll('token').pop() ?? null;
    const token = tokenRaw?.trim() ?? null;

    if (!authPayload || !origin) {
      this.loading = false;
      this.toastService.showError('Link inválido. Verifique e tente novamente.', 5000, 'top-center');
      this.router.navigate(['/login']);
      return;
    }

    this.clearPreviousMarketplaceSession();

    // UUID válido persiste; vazio/inválido limpa residual da aba
    if (token && validateTokenUUID(token)) {
      persistPendingOrdersToken(token);
    } else {
      clearPendingOrdersToken();
    }

    const mceProductCode = normalizeMceRedirectProductCode(redirectRaw);
    const hasMceProductRedirect = !!mceProductCode;
    const destinationPath = !hasMceProductRedirect
      ? resolveMceRedirectDestinationPath(redirectRaw)
      : null;
    const safeRedirect =
      destinationPath ??
      (!hasMceProductRedirect && isSafeInternalRedirect(redirectRaw)
        ? normalizeRedirect(redirectRaw)
        : null);

    if (mceProductCode) {
      this.mceRedirectProductService.saveIntent(mceProductCode);
    } else if (safeRedirect && isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(STORAGE_REDIRECT_AFTER_LOGIN, safeRedirect);
      } catch {
        // ignore
      }
    }

    this.loginService
      .externalChannelAccess({ authPayload: authPayload, origin: origin })
      .pipe(take(1))
      .subscribe({
        next: (response) =>
          this.handleSuccess(response, safeRedirect, hasMceProductRedirect),
        error: (err) => {
          console.error('externalChannelAccess error', err);
          clearPendingOrdersToken();
          this.toastService.showError(
            'Não foi possível validar seu acesso. Tente novamente.',
            MCE_REDIRECT_AFTER_ERROR_DELAY_MS,
            'top-center'
          );
          this.redirectToMceAfterError();
        },
      });
  }

  private redirectToMceAfterError(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => {
      if (this.externalChannelParentMessageService.notifyHubClosed()) {
        return;
      }

      window.location.href = CLARO_EMPRESAS_HOME_URL;
    }, MCE_REDIRECT_AFTER_ERROR_DELAY_MS);
  }

  private clearPreviousMarketplaceSession(): void {
    this.authService.logout();
    this.cartFacade.clearCart();
    this.headerService.resetActiveTopNavItem();
    clearExternalChannelSessionStorage();
  }

  private handleSuccess(
    response: ExternalChannelAccessResponse,
    safeRedirect: string | null,
    hasMceProductRedirect: boolean
  ): void {
    blockCartHydration();

    if (isPlatformBrowser(this.platformId)) {
      const originFromRequest = this.route.snapshot.queryParamMap.get('origin') ?? '';
      const responseToStore = withPersistedExternalChannelOrigin(
        response,
        originFromRequest
      );

      try {
        sessionStorage.setItem(
          STORAGE_EXTERNAL_CHANNEL_ACCESS,
          JSON.stringify(responseToStore)
        );
        if (originFromRequest.trim()) {
          sessionStorage.setItem(
            STORAGE_EXTERNAL_CHANNEL_ORIGIN,
            originFromRequest.trim()
          );
        }
      } catch {
        // ignore
      }
    }

    this.loading = false;

    if (shouldCompleteAddressFirst(response)) {
      this.router.navigate(['/external-channel-complete-address']);
      return;
    }

    this.hydrateExternalChannelLogin(response);
    this.bootstrapMobileOffersIfNeeded(response);
    this.cartFacade.clearExternalChannelCart();
    this.continueExternalChannelFlow(response, safeRedirect, hasMceProductRedirect);
  }

  private hydrateExternalChannelLogin(
    response: ExternalChannelAccessResponse
  ): void {
    const { user } = hydrateExternalChannelSession(
      response,
      this.authService,
      this.userFacade,
      isPlatformBrowser(this.platformId)
    );

    if (response.firstAccess) {
      const name =
        `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
        user?.displayName ||
        '';
      this.channelEventsService
        .sendUserRegisteredEvent(
          name,
          user?.document || '',
          user?.email || ''
        )
        .subscribe();
    }

    const campaign = readStoredExternalChannelCampaign();

    this.channelEventsService
      .sendUserLoginEvent({
        method: 'EXTERNAL_CHANNEL_ACCESS',
        userUuid: user?.uuid,
        login: user?.email || user?.login || user?.document,
        email: user?.email,
        ...(campaign ? { campaign } : {}),
      })
      .subscribe();
  }

  /** SUE = login móvel: seleciona CNPJ e carrega available-offers. */
  private bootstrapMobileOffersIfNeeded(
    response: ExternalChannelAccessResponse
  ): void {
    if (!isExternalChannelMobileType(response.type)) {
      return;
    }

    const clean = getExternalChannelMobileDocument(response);
    if (!clean) {
      return;
    }

    this.authService.syncSelectedDocument(clean);
    this.availableOffersFacade.loadAvailableOffers(clean);
  }

  private continueExternalChannelFlow(
    response: ExternalChannelAccessResponse,
    safeRedirect: string | null,
    hasMceProductRedirect: boolean
  ): void {
    if (hasMceProductRedirect) {
      this.mceRedirectProductService.tryAddFromStoredIntent().pipe(take(1)).subscribe(result => {
        if (result.handled && result.success) {
          this.router.navigate(['/checkout']);
          return;
        }
        this.router.navigate(['/']);
      });
      return;
    }

    const route = getPostAddressCompletionRoute(response, safeRedirect);
    const intermediatePages = ['/segment-selection', '/select-fixa-contract'];
    if (intermediatePages.includes(route)) {
      this.router.navigate([route]);
    } else {
      const finalRedirect = constructFinalRedirectWithToken();
      if (finalRedirect) {
        if (finalRedirect.queryParams) {
          this.router.navigate(finalRedirect.commands, {
            queryParams: finalRedirect.queryParams,
          });
        } else {
          this.router.navigate(finalRedirect.commands);
        }
        return;
      }
      this.router.navigate([route]);
    }
  }
}
